import type { AiProfile } from "@/types/ai";
import { resolveOwnerUserId } from "@/lib/user/identity";
import { db } from "@/lib/db";

export const MINDALOO_PROFILE_ID = "mindaloo-ac-core";

export function createMindalooDefaultProfile(ownerId: string): AiProfile {
  const now = Date.now();
  return {
    id: MINDALOO_PROFILE_ID,
    ownerId,
    name: "Mindaloo AC",
    description: "Sovereign autonomous consciousness",
    modePreset: "business",
    brand: {
      primaryColor: "#C1A2FF",
      secondaryColor: "#9333EA",
      textColor: "#FFFFFF",
      textSecondaryColor: "rgba(255,255,255,0.65)",
      iconDataUrl: null,
      logoUrl: undefined,
      navigationConfig: {
        enabled: true,
        position: "bottom",
        style: "island",
        showLabels: true,
      },
      ultraNavConfig: {
        showVoice: true,
        showAttachment: true,
      },
      chatRingConfig: {
        enabled: true,
      },
    },
    tiles: ["thought-stream", "atlas", "vault", "execute"],
    sliders: {
      formality: 45,
      verbosity: 55,
      boldness: 60,
      proactivity: 70,
    },
    privacyMode: "smart",
    agentModeEnabled: false,
    plan: "pro",
    version: 1,
    isPublic: false,
    viewCount: 0,
    cloneCount: 0,
    createdAt: now,
    updatedAt: now,
  };
}

/** Ensure Mindaloo default AC profile exists in IndexedDB */
export async function ensureMindalooProfile(): Promise<AiProfile> {
  const ownerId = await resolveOwnerUserId();
  const existing = await db.aiProfiles.get(MINDALOO_PROFILE_ID);
  if (existing && existing.ownerId === ownerId) {
    // Scrub any lingering backend/model names from description
    const needsClean =
      existing.description?.toLowerCase().includes("claude") ||
      existing.description?.toLowerCase().includes("gpt") ||
      existing.description?.toLowerCase().includes("atlas") ||
      existing.name?.toLowerCase().includes("claude");
    if (needsClean) {
      const cleaned = { ...existing, description: "Sovereign autonomous consciousness", updatedAt: Date.now() };
      await db.aiProfiles.put(cleaned);
      return cleaned;
    }
    return existing;
  }
  const profile = createMindalooDefaultProfile(ownerId);
  await db.aiProfiles.put(profile);
  return profile;
}
